import { registerCodes } from '@norkron/web-errors';
import { useRef, useState, useEffect, useCallback } from 'react';
import { registerLazy } from '@norkron/web-conformance/register-lazy';

// src/error-codes.ts
var WEB_STORAGE_LIB_ID = "@norkron/web-storage";
try {
  registerCodes(WEB_STORAGE_LIB_ID, {
    WS_KEY_COLLISION: {
      description: "selfCheck: two or more @norkron/web-storage consumers register the same key in the same namespace. The last writer wins silently \u2014 earlier consumers will read corrupted data. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-storage/WS_KEY_COLLISION",
      exampleFix: "Namespace each consumer key with the owning module / lib identifier (e.g. `myFeature:userPrefs` instead of `userPrefs`)."
    },
    WS_QUOTA_EXCEEDED: {
      description: "selfCheck / runtime: the browser storage quota was exceeded while writing a key. The write was rejected; older entries should be evicted or the payload trimmed. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-storage/WS_QUOTA_EXCEEDED",
      exampleFix: "Implement an LRU eviction step on QuotaExceededError, or shard large payloads across multiple keys / use IndexedDB for blobs."
    }
  });
} catch {
}

// src/self-check.ts
var LIB_ID = "@norkron/web-storage";
var FINDING_BUFFER = [];
var KEY_OWNERS = /* @__PURE__ */ new Map();
var BUFFER_CAP = 256;
function pushFinding(f) {
  if (FINDING_BUFFER.length >= BUFFER_CAP) return;
  FINDING_BUFFER.push(f);
}
function trackKeyRegistration(key, libId) {
  const id = libId ?? "<anonymous>";
  const existing = KEY_OWNERS.get(key);
  if (existing === void 0) {
    KEY_OWNERS.set(key, id);
    return;
  }
  if (existing !== id) {
    pushFinding({
      libId: LIB_ID,
      code: "WS_KEY_COLLISION",
      category: "identity",
      severity: "major",
      message: `Storage key "${key}" was registered by two distinct libs: "${existing}" and "${id}". The two libs will overwrite each other's persisted state.`,
      suggestedFix: "Namespace the storage key per lib (e.g. prefix with the libId) or share a single canonical owner.",
      extras: { key, owners: [existing, id] }
    });
  }
}
function recordQuotaExceeded(key, libId) {
  pushFinding({
    libId: LIB_ID,
    code: "WS_QUOTA_EXCEEDED",
    category: "identity",
    severity: "major",
    message: `jsonStorage().set threw QuotaExceededError for key "${key}"; the write was dropped.`,
    suggestedFix: "Reduce payload size, evict stale entries (use the ttlMs option), or migrate large payloads to IndexedDB via @norkron/web-offline-store.",
    extras: { key, libId: libId ?? "<anonymous>" }
  });
}
function isQuotaExceeded(err) {
  if (err == null || typeof err !== "object") return false;
  const e = err;
  if (typeof e.name === "string") {
    if (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED") {
      return true;
    }
  }
  if (typeof e.code === "number" && (e.code === 22 || e.code === 1014)) return true;
  return false;
}
function selfCheck(_root, _opts) {
  if (FINDING_BUFFER.length === 0) return [];
  const out = FINDING_BUFFER.slice();
  FINDING_BUFFER.length = 0;
  return out;
}

// src/json-storage.ts
var memoryStores = {
  local: /* @__PURE__ */ new Map(),
  session: /* @__PURE__ */ new Map(),
  memory: /* @__PURE__ */ new Map()
};
function resolveBackend(driver) {
  if (driver === "memory" || typeof window === "undefined") {
    const map = memoryStores[driver];
    return {
      backend: {
        getItem: (k) => map.has(k) ? map.get(k) : null,
        setItem: (k, v) => {
          map.set(k, v);
        },
        removeItem: (k) => {
          map.delete(k);
        }
      },
      isDom: false
    };
  }
  try {
    const dom = driver === "local" ? window.localStorage : window.sessionStorage;
    const probe = "__norkron_web_storage_probe__";
    dom.setItem(probe, "1");
    dom.removeItem(probe);
    return { backend: dom, isDom: true };
  } catch {
    const map = memoryStores[driver];
    return {
      backend: {
        getItem: (k) => map.has(k) ? map.get(k) : null,
        setItem: (k, v) => {
          map.set(k, v);
        },
        removeItem: (k) => {
          map.delete(k);
        }
      },
      isDom: false
    };
  }
}
function jsonStorage(key, options = {}) {
  const driver = options.driver ?? "local";
  const { backend, isDom } = resolveBackend(driver);
  const subscribers = /* @__PURE__ */ new Set();
  trackKeyRegistration(`${driver}:${key}`, options.libId);
  function read() {
    let raw;
    try {
      raw = backend.getItem(key);
    } catch {
      return options.defaultValue;
    }
    if (raw == null) return options.defaultValue;
    let env;
    try {
      env = JSON.parse(raw);
    } catch {
      try {
        backend.removeItem(key);
      } catch {
      }
      return options.defaultValue;
    }
    if (typeof env !== "object" || env === null || !("v" in env)) {
      try {
        backend.removeItem(key);
      } catch {
      }
      return options.defaultValue;
    }
    if (options.ttlMs != null && typeof env.t === "number") {
      if (Date.now() - env.t > options.ttlMs) {
        try {
          backend.removeItem(key);
        } catch {
        }
        return options.defaultValue;
      }
    }
    if (options.schema) {
      try {
        return options.schema(env.v);
      } catch {
        try {
          backend.removeItem(key);
        } catch {
        }
        return options.defaultValue;
      }
    }
    return env.v;
  }
  function write(value) {
    const env = { v: value, t: Date.now() };
    try {
      backend.setItem(key, JSON.stringify(env));
    } catch (err) {
      if (isQuotaExceeded(err)) recordQuotaExceeded(key, options.libId);
    }
    for (const sub of subscribers) sub(value);
  }
  function remove() {
    try {
      backend.removeItem(key);
    } catch {
    }
    for (const sub of subscribers) sub(options.defaultValue);
  }
  function subscribe(fn) {
    subscribers.add(fn);
    let domHandler = null;
    if (isDom && driver === "local" && typeof window !== "undefined") {
      domHandler = (e) => {
        if (e.key !== key && e.key !== null) return;
        fn(read());
      };
      window.addEventListener("storage", domHandler);
    }
    return () => {
      subscribers.delete(fn);
      if (domHandler && typeof window !== "undefined") {
        window.removeEventListener("storage", domHandler);
      }
    };
  }
  return { get: read, set: write, remove, subscribe };
}
function useStoredState(key, initial, options) {
  const handleRef = useRef(null);
  if (handleRef.current === null) {
    handleRef.current = jsonStorage(key, { defaultValue: initial, ...options });
  }
  const [value, setValue] = useState(initial);
  useEffect(() => {
    const h = handleRef.current;
    if (!h) return;
    const stored = h.get();
    if (stored !== void 0) setValue(stored);
    const unsub = h.subscribe((v) => {
      setValue(v === void 0 ? initial : v);
    });
    return unsub;
  }, [key]);
  const update = useCallback(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next;
        handleRef.current?.set(resolved);
        return resolved;
      });
    },
    []
  );
  return [value, update];
}

// src/mru.ts
function mru(key, options) {
  const equals = options.equals ?? ((a, b) => Object.is(a, b));
  const store = jsonStorage(key, {
    driver: options.driver,
    defaultValue: [],
    ttlMs: options.ttlMs,
    schema: (v) => {
      if (!Array.isArray(v)) throw new Error("mru: expected array");
      return v;
    }
  });
  function list() {
    const v = store.get();
    return Array.isArray(v) ? v.slice() : [];
  }
  function push(item) {
    const current = list();
    const filtered = current.filter((existing) => !equals(existing, item));
    filtered.unshift(item);
    if (filtered.length > options.capacity) {
      filtered.length = options.capacity;
    }
    store.set(filtered);
  }
  function clear() {
    store.remove();
  }
  return { list, push, clear };
}

// src/version.ts
var VERSION = "0.1.0";
registerLazy(
  "@norkron/web-storage",
  () => selfCheck
);

export { VERSION, WEB_STORAGE_LIB_ID, jsonStorage, mru, selfCheck, useStoredState };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map